// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { xTranslate } from "@steiner/XDL/Utilities/translate";
import React, { FC, PropsWithChildren, useEffect, useRef, useState } from "react";

export interface TableCounterStyles {
    container?: InjectableCss;
    headingContainer?: InjectableCss;
    rangeAndNavigationContainer?: InjectableCss;
    heading?: TypographyPresentationProps;
    displayRange?: TypographyProps;
    viewAllLink?: LinkPresentationProps;
    tableAccordionSectionProps?: AccordionSectionPresentationProps;
    cartLinesHeaderContainer?: InjectableCss;
}

type Props = {
    headingTitle?: string;
    countText?: string;
    viewAllUrl?: string;
    accordionOnSmallScreen?: boolean;

    // count depend on pagination
    pagination?: Readonly<PaginationModel>;

    // dynamic scroll count
    watchResize?: boolean;
    totalItemsCount?: number;
    extendedStyles?: TableCounterStyles;

    // Actions showing on the right side of the table header
    actions?: any;
};

export const tableCounterStyles: TableCounterStyles = {};

const TableCounter: FC<PropsWithChildren<Props>> = ({
    pagination,
    viewAllUrl,
    children,
    headingTitle,
    countText,
    accordionOnSmallScreen = true,
    totalItemsCount,
    watchResize = false,
    extendedStyles,
    actions,
}) => {
    const [styles] = useState(() => mergeToNew(tableCounterStyles, extendedStyles));

    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));

    const [visibleCartLines, setVisibleCartLines] = useState({ start: 1, end: 1 });
    const containerRef = useRef<HTMLDivElement>(null);

    const calculateVisibleRange = (scrollHeight: number, scrollTop: number, offsetHeight: number) => {
        const totalItems = totalItemsCount || 1;
        const itemHeight = scrollHeight / totalItems;
        const visibleCount = Math.round(offsetHeight / itemHeight);
        const start = Math.round(scrollTop / itemHeight) + 1;
        const end = Math.min(start + visibleCount - 1, totalItems);

        return { start, end };
    };

    useEffect(() => {
        if (!totalItemsCount || !containerRef.current || !watchResize) {
            return;
        }

        const { scrollHeight, scrollTop, offsetHeight } = containerRef.current;
        setVisibleCartLines(calculateVisibleRange(scrollHeight, scrollTop, offsetHeight));
    }, [totalItemsCount, containerRef]);

    useEffect(() => {
        if (pagination && pagination.page && pagination.pageSize && !watchResize) {
            setVisibleCartLines({
                start: (pagination.page - 1) * pagination.pageSize + 1,
                end: Math.min(pagination.page * pagination.pageSize, pagination?.totalItemCount || 0),
            });
        }
    }, [pagination]);

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (watchResize && event.target instanceof HTMLDivElement) {
            const { scrollHeight, scrollTop, offsetHeight } = event.target;
            setVisibleCartLines(calculateVisibleRange(scrollHeight, scrollTop, offsetHeight));
        }
    };

    const heading = headingTitle && <Typography {...styles.heading}>{headingTitle}</Typography>;

    const displayRange = (
        <Typography {...styles.displayRange}>
            {xTranslate(
                "Showing {0} out of {1}",
                <Typography weight="bold">{`${visibleCartLines.start}-${visibleCartLines.end}`}</Typography>,
                <Typography weight="bold">
                    {pagination?.totalItemCount || totalItemsCount}
                    {countText && ` ${countText}`}
                </Typography>,
            )}
        </Typography>
    );

    const viewAllLink = viewAllUrl && (
        <Link {...styles.viewAllLink} href={viewAllUrl}>
            {translate("View All")}
        </Link>
    );

    return (
        <StyledWrapper {...styles.container}>
            {isTabletScreen && accordionOnSmallScreen ? (
                <ManagedAccordionSection
                    data-test-selector="reorderPad"
                    title={
                        <StyledWrapper {...styles.headingContainer}>
                            {heading}
                            {viewAllLink}
                            {actions}
                        </StyledWrapper>
                    }
                    initialExpanded={true}
                    {...styles.tableAccordionSectionProps}
                >
                    {children}
                </ManagedAccordionSection>
            ) : (
                <>
                    <StyledWrapper {...styles.headingContainer}>
                        {heading}
                        <StyledWrapper {...styles.rangeAndNavigationContainer}>
                            {displayRange}
                            {viewAllLink}
                            {actions}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper
                        {...(watchResize && styles.cartLinesHeaderContainer)}
                        onScroll={handleScroll}
                        ref={containerRef}
                    >
                        {children}
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

export default TableCounter;
